import { computed, onActivated, onBeforeUnmount, onDeactivated, ref, watch } from 'vue'
import {
  getTranscriptSessions,
  getTranscriptSentences,
  getTranscriptPlayback,
  prepareTranscriptAudio,
} from '@/api/meetingTranscript'

import { previousSentenceCursor } from '../components/meetingTranscript/transcriptTime'

const dataOf = (response) => {
  if (response?.data == null) throw new Error('查询失败，请重试')
  return response.data
}

export function useMeetingTranscript(meetingCode) {
  const sessions = ref([])
  const capture = ref('')
  const keyword = ref('')
  const schedule = ref('')
  const schedules = ref([])
  const items = ref([])
  const cursor = ref(null)
  const playback = ref({ status: 'UNAVAILABLE' })
  const loading = ref(false)
  const sessionsLoading = ref(false)
  const playbackLoading = ref(false)
  const error = ref('')
  const sessionError = ref('')
  const playerKey = ref(0)
  const seekRequest = ref(null)
  let scopeVersion = 0
  let sessionVersion = 0
  let listVersion = 0
  let mediaVersion = 0
  let disposed = false
  let active = true
  let pollTimer = null
  const clearPoll = () => {
    if (pollTimer) clearTimeout(pollTimer)
    pollTimer = null
  }
  const session = computed(() => sessions.value.find((item) => (item.captureSessionId || '') === capture.value))

  async function loadPage(append = false, anchorCursor = null) {
    const scope = scopeVersion
    if (append && (loading.value || !cursor.value)) return
    const version = ++listVersion
    loading.value = true
    error.value = ''
    if (!append) {
      items.value = []
      cursor.value = null
    }
    try {
      const data = dataOf(
        await getTranscriptSentences(meetingCode.value, {
          captureSessionId: capture.value || undefined,
          scheduleCode: schedule.value || undefined,
          keyword: keyword.value.trim() || undefined,
          cursor: append ? cursor.value : anchorCursor || undefined,
          limit: 100,
        })
      )
      if (disposed || scope !== scopeVersion || version !== listVersion) return
      const existing = append ? items.value : []
      const ids = new Set(existing.map((item) => item.id))
      items.value = [...existing, ...data.items.filter((item) => !ids.has(item.id))]
      cursor.value = data.nextCursor || null
      schedules.value = data.scheduleCodes || []
    } catch (failure) {
      if (!disposed && scope === scopeVersion && version === listVersion)
        error.value = failure.message || failure.msg || '原文查询失败'
    } finally {
      if (!disposed && scope === scopeVersion && version === listVersion) loading.value = false
    }
  }

  async function refreshPlayback() {
    const scope = scopeVersion
    const version = ++mediaVersion
    playbackLoading.value = true
    try {
      const data = dataOf(await getTranscriptPlayback(meetingCode.value, capture.value || undefined))
      if (!disposed && scope === scopeVersion && version === mediaVersion) playback.value = data
    } catch (failure) {
      if (!disposed && scope === scopeVersion && version === mediaVersion)
        playback.value = { status: 'SOURCE_UNAVAILABLE' }
    } finally {
      if (!disposed && scope === scopeVersion && version === mediaVersion) playbackLoading.value = false
    }
  }

  watch(playback, (value) => {
    clearPoll()
    if (active && !disposed && ['QUEUED', 'PROCESSING'].includes(value.status))
      pollTimer = setTimeout(() => {
        pollTimer = null
        refreshPlayback()
      }, 5000)
  })

  async function prepareAudio() {
    if (playbackLoading.value || !playback.value.canPrepare) return
    clearPoll()
    const scope = scopeVersion
    const version = ++mediaVersion
    playbackLoading.value = true
    try {
      const data = dataOf(await prepareTranscriptAudio(meetingCode.value, capture.value))
      if (!disposed && scope === scopeVersion && version === mediaVersion) playback.value = data
    } catch (failure) {
      if (!disposed && scope === scopeVersion && version === mediaVersion)
        playback.value = { status: 'SOURCE_UNAVAILABLE' }
    } finally {
      if (!disposed && scope === scopeVersion && version === mediaVersion) playbackLoading.value = false
    }
  }

  function selectCapture(value) {
    clearPoll()
    scopeVersion++
    capture.value = value
    keyword.value = ''
    schedule.value = ''
    schedules.value = []
    seekRequest.value = null
    playback.value = { status: 'UNAVAILABLE' }
    playerKey.value++
    loadPage()
    refreshPlayback()
  }

  async function initialize() {
    clearPoll()
    const scope = ++scopeVersion
    const sessionRequest = ++sessionVersion
    ++listVersion
    ++mediaVersion
    playerKey.value++
    sessions.value = []
    items.value = []
    schedules.value = []
    cursor.value = null
    capture.value = ''
    schedule.value = ''
    keyword.value = ''
    playback.value = { status: 'UNAVAILABLE' }
    loading.value = false
    playbackLoading.value = false
    error.value = ''
    sessionError.value = ''
    sessionsLoading.value = true
    if (!meetingCode.value) {
      sessionError.value = '缺少会议编号'
      sessionsLoading.value = false
      return
    }
    try {
      const data = dataOf(await getTranscriptSessions(meetingCode.value))
      if (disposed || scope !== scopeVersion) return
      sessions.value = data
      if (data.length) selectCapture(data[0].captureSessionId || '')
    } catch (failure) {
      if (!disposed && scope === scopeVersion) sessionError.value = failure.message || failure.msg || '场次查询失败'
    } finally {
      if (!disposed && sessionRequest === sessionVersion) sessionsLoading.value = false
    }
  }

  async function seekSentence(item, range) {
    if (!range) return
    seekRequest.value = { id: item.id, offsetMs: range.start, nonce: Date.now() }
    // Open the forward context, rather than leaving a filtered search fragment playing.
    if (keyword.value || schedule.value) {
      keyword.value = ''
      schedule.value = ''
      await loadPage(false, previousSentenceCursor(item.id))
    }
  }

  watch(
    meetingCode,
    () => {
      if (active) initialize()
    },
    { immediate: true }
  )
  onActivated(() => {
    active = true
  })
  onDeactivated(() => {
    active = false
    clearPoll()
    ++scopeVersion
    ++listVersion
    ++mediaVersion
    loading.value = false
    playbackLoading.value = false
    sessionsLoading.value = false
  })
  onBeforeUnmount(() => {
    disposed = true
    clearPoll()
    ++scopeVersion
  })
  return {
    sessions,
    session,
    capture,
    keyword,
    schedule,
    schedules,
    items,
    cursor,
    playback,
    loading,
    sessionsLoading,
    playbackLoading,
    error,
    sessionError,
    playerKey,
    seekRequest,
    initialize,
    selectCapture,
    loadPage,
    refreshPlayback,
    prepareAudio,
    seekSentence,
  }
}
